import { Link } from "react-router-dom";
import Layout from "@/components/feature/Layout";
import PageHeader from "@/components/feature/PageHeader";
import usePageMeta from "@/hooks/usePageMeta";
import { siteName, siteSlogan, siteIntro } from "@/mocks/mangaData";

const principles = [
  {
    icon: "ri-focus-3-line",
    title: "聚焦剧情主线",
    desc: "以主线走向为纲，把分散在多话的信息重新组织，帮助读者快速建立完整的剧情认知。",
  },
  {
    icon: "ri-file-list-3-line",
    title: "逐话认真梳理",
    desc: "关键分话逐话拆解，整理节点、人物反应与伏笔信号，做到有据可依、条理清晰。",
  },
  {
    icon: "ri-shield-check-line",
    title: "明确读者定位",
    desc: "本站为读者向剧情追踪与解读内容，非官方发布渠道，一切以官方更新内容为准。",
  },
  {
    icon: "ri-refresh-line",
    title: "持续更新追踪",
    desc: "随官方更新及时同步分话进度与结局线索，让追更的读者始终不错过关键节点。",
  },
];

const testimonials = [
  {
    name: "追更老粉·小鹿",
    role: "主线爱好者",
    text: "自从看了这里的剧情主线梳理，把之前零散看的分话全串起来了，身世那条线终于看明白了。",
  },
  {
    name: "角色党·阿澄",
    role: "角色解析读者",
    text: "角色档案整理得很细，结局线索那块写得尤其清楚，每周更新我都会回来刷一遍。",
  },
  {
    name: "新读者·木木",
    role: "刚入坑",
    text: "刚入坑不知道从哪话开始看，按这里的追更时间轴补，一点都不迷路，太适合新读者了。",
  },
];

export default function About() {
  usePageMeta({
    title: "关于我们 | 甜蜜惩罚漫画剧情追踪站",
    description:
      "关于甜蜜惩罚漫画剧情追踪站：我们是一群热爱甜蜜惩罚漫画剧情的读者，专注主线梳理、分话解读、角色盘点与结局线索追踪，与你一起追更剧情不再迷路。",
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "AboutPage",
      name: "关于我们",
      inLanguage: "zh-CN",
      mainEntity: {
        "@type": "Organization",
        name: siteName,
        description: siteIntro,
        slogan: siteSlogan,
      },
    },
  });

  return (
    <Layout>
      <PageHeader
        title="关于我们"
        subtitle="我们是一群热爱《甜蜜惩罚漫画》的读者，因为不想在复杂的剧情线里迷路，所以搭起了这个剧情追踪站，和大家一起把主线、人物与结局线索看得明明白白。"
        crumbs={[{ label: "首页", path: "/" }, { label: "关于我们" }]}
      />

      {/* 品牌定位 */}
      <section className="py-12 md:py-16">
        <div className="mx-auto max-w-7xl px-4 md:px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
            <div>
              <span className="font-label text-xs md:text-sm font-semibold text-primary-600 uppercase tracking-wider">
                网站定位
              </span>
              <h2 className="mt-2 font-heading text-2xl md:text-3xl font-bold">
                {siteName}，{siteSlogan}
              </h2>
              <p className="mt-4 text-sm md:text-[15px] leading-7 text-foreground-700">{siteIntro}</p>
              <p className="mt-3 text-sm md:text-[15px] leading-7 text-foreground-700">
                我们不替代官方，也不搬运剧情，而是做「读者友好的二次整理」——把散落在许多分话里的信息，按照主线、角色与节点重新编排，让新老读者都能快速上手、持续追更。
              </p>
            </div>
            <div className="overflow-hidden rounded-lg border border-background-200">
              <img
                src="https://readdy.ai/api/search-image?query=An%20inviting%20reading%20nook%20with%20an%20open%20manga%20volume%2C%20warm%20lamp%20light%2C%20a%20cup%20of%20tea%20and%20scattered%20rose%20petals%20on%20a%20cozy%20wooden%20table%2C%20dreamy%20romantic%20ink-wash%20editorial%20illustration%2C%20deep%20rose%20and%20warm%20amber%20palette%2C%20soft%20nostalgic%20lighting%2C%20minimal%20elegant%20composition%2C%20high%20detail&width=1000&height=720&seq=about-team&orientation=landscape"
                alt="甜蜜惩罚漫画剧情追踪站 关于我们配图"
                className="h-64 md:h-80 w-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* 内容原则 */}
      <section className="py-12 md:py-16 bg-background-100/60 border-y border-background-200">
        <div className="mx-auto max-w-7xl px-4 md:px-6">
          <div className="max-w-2xl mb-9">
            <span className="font-label text-xs md:text-sm font-semibold text-primary-600 uppercase tracking-wider">
              内容原则
            </span>
            <h2 className="mt-2 font-heading text-2xl md:text-3xl font-bold">我们坚持这样梳理剧情</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {principles.map((p) => (
              <div key={p.title} className="rounded-lg border border-background-200 bg-background-50 p-6">
                <span className="grid w-11 h-11 place-items-center rounded-md bg-primary-500 text-background-50 text-xl">
                  <i className={p.icon}></i>
                </span>
                <h3 className="mt-4 font-heading text-lg font-semibold text-foreground-950">{p.title}</h3>
                <p className="mt-2 text-sm leading-6 text-foreground-700">{p.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 用户口碑 */}
      <section className="py-12 md:py-16">
        <div className="mx-auto max-w-7xl px-4 md:px-6">
          <div className="max-w-2xl mb-9">
            <span className="font-label text-xs md:text-sm font-semibold text-primary-600 uppercase tracking-wider">
              读者口碑
            </span>
            <h2 className="mt-2 font-heading text-2xl md:text-3xl font-bold">追更路上，与大家同行</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {testimonials.map((t) => (
              <figure key={t.name} className="rounded-lg border border-background-200 bg-background-50 p-6">
                <i className="ri-double-quotes-l text-3xl text-accent-400"></i>
                <blockquote className="mt-3 text-sm leading-7 text-foreground-700">{t.text}</blockquote>
                <figcaption className="mt-5 flex items-center gap-3">
                  <span className="grid w-10 h-10 place-items-center rounded-full bg-secondary-100 text-secondary-900 font-heading text-sm font-bold">
                    {t.name[0]}
                  </span>
                  <div>
                    <p className="text-sm font-medium text-foreground-950">{t.name}</p>
                    <p className="text-xs text-foreground-500">{t.role}</p>
                  </div>
                </figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>

      {/* 联系方式 */}
      <section className="py-12 md:py-16 bg-background-100/60 border-t border-background-200">
        <div className="mx-auto max-w-4xl px-4 md:px-6 text-center">
          <span className="font-label text-xs md:text-sm font-semibold text-primary-600 uppercase tracking-wider">
            联系我们
          </span>
          <h2 className="mt-2 font-heading text-2xl md:text-3xl font-bold">与我们保持联系</h2>
          <p className="mt-3 text-sm md:text-[15px] leading-7 text-foreground-700">
            有剧情疑问、追更建议或想指出的疏漏？欢迎通过邮箱联系，或订阅更新第一时间获取剧情追踪内容。
          </p>
          <div className="mt-7 flex flex-col sm:flex-row items-center justify-center gap-3">
            <a
              href="mailto:hello@tianmi-follow.example"
              className="inline-flex items-center gap-2 rounded-full bg-primary-500 text-background-50 px-6 py-3 text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap"
            >
              <i className="ri-mail-line text-base leading-none"></i>
              hello@tianmi-follow.example
            </a>
            <Link
              to="/"
              className="inline-flex items-center gap-2 rounded-full border border-background-300 text-foreground-800 px-6 py-3 text-sm font-medium hover:border-primary-400 hover:text-primary-700 transition-colors whitespace-nowrap"
            >
              <i className="ri-heart-3-line text-base leading-none"></i>
              返回首页
            </Link>
          </div>
          <p className="mt-6 text-xs text-foreground-500">
            本站为读者向内容站点，所有剧情分析均基于已公开分话整理，非官方发布渠道。
          </p>
        </div>
      </section>
    </Layout>
  );
}